import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Headphones, MapPin } from "lucide-react";
import axiosPublic from "../../api/axiosConfig";

const DjPlaceSelection = ({ onDjChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const urlDjId = queryParams.get("dj");
  const urlPlaceId = queryParams.get("place");

  const [places, setPlaces] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [selectedDjId, setSelectedDjId] = useState("");

  useEffect(() => {
    const allowedParams = ["dj", "place"];
    const actualParams = Array.from(queryParams.keys());

    const hasInvalidParams = actualParams.some(
      (param) => !allowedParams.includes(param),
    );

    if (hasInvalidParams) {
      toast.error("Invalid URL parameter");
      navigate("/", { replace: true });
      return;
    }

    const fetchPlacesWithDj = async () => {
      try {
        const response = await axiosPublic.get("/place/active");

        setPlaces(response.data);

        if (urlDjId) {
          const djExists = response.data.find(
            (place) => place.dj.id === Number(urlDjId),
          );

          if (djExists) {
            setSelectedDjId(urlDjId);
            setSelectedPlaceId(djExists.id.toString());

            if (onDjChange) {
              onDjChange(djExists.dj);
            }
          } else {
            toast.error("DJ not found or offline");
            navigate("/", { replace: true });
          }
        }

        if (urlPlaceId) {
          const placeExists = response.data.find(
            (place) => place.id === Number(urlPlaceId),
          );

          if (placeExists) {
            setSelectedPlaceId(urlPlaceId);

            if (placeExists && placeExists.dj) {
              setSelectedDjId(placeExists.dj.id.toString());

              if (onDjChange) {
                onDjChange(placeExists.dj);
              }
            } else {
              toast.error("No DJ is currently playing at this place");
              navigate("/", { replace: true });
            }
          } else {
            toast.error("Place not found or no DJ is here now");
            navigate("/", { replace: true });
          }
        }
      } catch (error) {
        toast.error("Failed to load places and Djs");
        console.error(error);
      }
    };

    fetchPlacesWithDj();
  }, [urlDjId, urlPlaceId]);

  const handlePlaceChange = (event) => {
    const placeId = event.target.value;
    setSelectedPlaceId(placeId);

    if (placeId === "") {
      setSelectedDjId("");
      if (onDjChange) {
        onDjChange(null);
      }
      return;
    }

    const selectedPlace = places.find((place) => place.id === Number(placeId));
    if (selectedPlace) {
      setSelectedDjId(selectedPlace.dj.id);
      if (onDjChange) {
        onDjChange(selectedPlace.dj);
      }
    }
  };

  const handleDjChange = (event) => {
    const djId = event.target.value;
    setSelectedDjId(djId);

    if (djId === "") {
      setSelectedPlaceId("");
      if (onDjChange) {
        onDjChange(null);
      }
      return;
    }

    const selectedPlace = places.find((place) => place.dj.id === Number(djId));
    if (selectedPlace) {
      setSelectedPlaceId(selectedPlace.id);
    }

    if (onDjChange) {
      onDjChange(selectedPlace.dj);
    }
  };

  return (
    <div className="p-4 max-w-lg mx-auto rounded-2xl shadow-md">
      <div className="mb-4">
        <label
          className="flex items-center text-textMain font-medium mb-2"
          htmlFor="place"
        >
          <MapPin size={18} className="mr-2 text-accent" /> Select Place:
        </label>
        <div className="relative">
          <select
            id="place"
            value={selectedPlaceId}
            onChange={handlePlaceChange}
            className="w-full p-3 rounded-xl outline outline-2 outline-secondary bg-notificationList text-textMain focus:outline-none focus:ring-2 focus:ring-accent appearance-none"
          >
            <option value="">Choose your place</option>
            {places.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name}
              </option>
            ))}
          </select>
          <span className="absolute inset-y-0 right-3 flex items-center text-textMuted pointer-events-none">
            <ChevronDown size={16} className="aspect-square" />
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center my-2">
        <span className="text-white text-lg font-bold tracking-wide">OR</span>
      </div>

      <div>
        <label
          className="flex items-center text-textMain font-medium mb-2"
          htmlFor="dj"
        >
          <Headphones size={18} className="mr-2 text-accent" /> Select DJ:
        </label>
        <div className="relative">
          <select
            id="dj"
            value={selectedDjId}
            onChange={handleDjChange}
            className="w-full p-3 rounded-xl outline outline-2 outline-secondary bg-notificationList text-textMain focus:outline-none focus:ring-2 focus:ring-accent appearance-none"
          >
            <option value="">Choose your DJ</option>
            {places.map((place) => (
              <option key={place.dj.id} value={place.dj.id}>
                {place.dj.name} | {place.dj.currentPrice} PLN
              </option>
            ))}
          </select>
          <span className="absolute inset-y-0 right-3 flex items-center text-textMuted pointer-events-none">
            <ChevronDown size={16} className="aspect-square" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default DjPlaceSelection;
